<template>
  <div class="!pt-0">
    <div class="mx-auto flex flex-col max-h-screen pt-16 pb-2.5">
      <div class="my-2.5">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              Subcontractor Claims Dashboard
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              Manage and track subcontractor progress claims
            </p>
          </div>

          <div class="flex items-center space-x-3">
            <div class="flex items-center space-x-2">
              <button
                type="button"
                class="btn"
                :class="viewMode === 'month' ? 'btn--primary-blue' : 'btn--secondary-blue'"
                data-test="month-view-toggle"
                @click="viewMode = 'month'"
              >
                View by Month
              </button>

              <button
                type="button"
                class="btn"
                :class="viewMode === 'packages' ? 'btn--primary-blue' : 'btn--secondary-blue'"
                data-test="packages-view-toggle"
                @click="viewMode = 'packages'"
              >
                View All
              </button>
            </div>

            <button
              v-if="project && !project.feature_flag_subcontractor_claims_lite"
              type="button"
              class="btn btn--secondary-blue"
              @click="showMassIssueClaimModal = true"
            >
              Mass Issue Claims for Month
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="!fetchDashboardAction.firstLoad.value && fetchDashboardAction.is(States.LOADING)"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="flex flex-col items-center py-16">
          <AppSpinner class="h-8 w-8 mb-4" />

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            Loading subcontractors...
          </h3>
        </div>
      </div>

      <div
        v-else-if="!hasPackages"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="px-6 py-16 text-center">
          <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            No packages found
          </h3>

          <p class="text-sm text-gray-500 mb-6">
            No subcontractors have been assigned to packages for this project.
          </p>
        </div>
      </div>

      <PackagesMonthList
        v-else-if="viewMode === 'month'"
        v-model:selected-month="selectedMonth"
        :project-id="projectId"
        @create-claim="createClaim"
        @issue-claim="issueClaim"
      />

      <PackagesList
        v-else
        :project-id="projectId"
        @create-claim="createClaim"
        @issue-claim="issueClaim"
      />
    </div>

    <CreateClaimModal
      v-if="showCreateClaimModal"
      :selected-month="selectedMonthAsDate"
      :project-supplier="selectedProjectSupplier"
      :package="selectedPackage"
      :project-id="projectId"
      :latest-claim="selectedLatestClaim"
      @close="showCreateClaimModal = false"
    />

    <MassIssueClaimModal
      v-if="showMassIssueClaimModal"
      :selected-month="selectedMonthAsDate"
      :project-id="projectId"
      @close="showMassIssueClaimModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import CreateClaimModal from '@/components/Subcontract/SubcontractorClaimsDashboard/CreateClaimModal.vue';
import MassIssueClaimModal from '@/components/Subcontract/SubcontractorClaimsDashboard/MassIssueClaimModal.vue';
import PackagesList from '@/components/Subcontract/SubcontractorClaimsDashboard/PackagesList.vue';
import PackagesMonthList from '@/components/Subcontract/SubcontractorClaimsDashboard/PackagesMonthList.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import useDayQueryParam from '@/composables/useDayQueryParam';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { timeZone } from '@/helpers';
import Claim from '@/models/Claim';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { useClaimsStore } from '@/store/claims';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { useRouteHash } from '@vueuse/router';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

useBoqLockRedirect();
const packagesStore = usePackagesStore();
const claimsStore = useClaimsStore();
const projectsStore = useProjectsStore();
const project = computed(() => projectsStore.project);
const fetchDashboardAction = useStoreApiAction(packagesStore.fetchSubcontractorDashboard);
const selectedDate = useDayQueryParam();
const selectedMonth = ref(DateTime.fromJSDate(selectedDate.value).toFormat('yyyy-MM'));
const viewMode = ref<'month' | 'packages'>('month');
const router = useRouter();
const route = useRoute();

watch(viewMode, (mode) => {
  router.replace({ hash: `#${mode}` });
});

watch(() => route.hash, (newHash) => {
  if(newHash) {
    viewMode.value = newHash.slice(1) as 'month' | 'packages';
  }
}, { immediate: true });

const selectedMonthAsDate = computed(() => {
  if(viewMode.value === 'packages') {
    return DateTime.now().startOf('month');
  }

  return DateTime.fromFormat(selectedMonth.value, 'yyyy-MM');
});

watch(() => project.value?.id, (projectId) => {
  if(projectId) {
    fetchDashboardAction.request(projectId, {
      month: viewMode.value === 'packages' ?
        DateTime.now().toFormat('yyyy-MM') :
        selectedMonth.value,
    });
  }
}, { immediate: true });

watch(viewMode, (mode) => {
  if(!project.value?.id || mode !== 'month') {
    return;
  }

  fetchDashboardAction.request(project.value.id, {
    month: selectedMonth.value,
  });
});

const hasPackages = computed(() => {
  return packagesStore.models
    .where('project_id', Number(props.projectId))
    .get()
    .length > 0;
});

const showCreateClaimModal = ref(false);
const selectedPackage = ref<Package | undefined>();
const selectedProjectSupplier = ref<ProjectSupplier | undefined>();
const selectedLatestClaim = ref<Claim | undefined>();

const createClaim = (data: { projectSupplier: ProjectSupplier; packageData: Package; latestClaim?: Claim }) => {
  selectedProjectSupplier.value = data.projectSupplier;
  selectedPackage.value = data.packageData;
  selectedLatestClaim.value = data.latestClaim;
  showCreateClaimModal.value = true;
};

const issueClaim = async (data: { claim: Claim; email: string }) => {
  claimsStore.issueSubcontractorClaim(projectsStore.project.id, data.claim.id, {
    email: data.email,
    draft_issued_at_time_zone: timeZone,
  }).then(() => {
    Swal.fire({
      title: 'Claim Issued',
      text: 'The claim has been successfully sent to the subcontractor.',
      icon: 'success',
      confirmButtonColor: '#10b981',
    });
  }).catch((error) => {
    if(error.status === 422) {
      Swal.fire({
        title: 'Error',
        text: error.data.message,
        icon: 'error',
      });
    }
  });
};

const showMassIssueClaimModal = ref(false);
</script>

<style scoped></style>
